import { io, Socket } from "socket.io-client";
import { SOCKET_URL } from "../config/api";

console.log("🔌 Socket URL:", SOCKET_URL);

interface SocketEventHandlers {
  onOrderUpdated?: (data: any) => void;
  onOrderReceived?: (data: any) => void;
  onTableUpdated?: (data: any) => void;
  onFoodItemUpdated?: (data: any) => void;
  onDashboardUpdated?: (data: any) => void;
  onAuthenticated?: (data: any) => void;
  onAuthError?: (data: any) => void;
  onError?: (data: any) => void;
  onJoinedRestaurant?: (data: any) => void;
}

class SocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private eventHandlers: SocketEventHandlers = {};
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  constructor() {
    this.setupSocket();
  }

  private setupSocket() {
    try {
      console.log("🔌 Setting up socket connection to:", SOCKET_URL);

      this.socket = io(SOCKET_URL, {
        transports: ["polling"], // Use polling only for reliability with HTTPS
        autoConnect: false,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
        timeout: 15000,
        forceNew: true,
      });

      this.setupEventListeners();
    } catch (error) {
      console.error("Failed to setup socket:", error);
    }
  }

  private setupEventListeners() {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      console.log("🔌 Socket connected");
      this.isConnected = true;
      this.reconnectAttempts = 0;
    });

    this.socket.on("disconnect", () => {
      console.log("🔌 Socket disconnected");
      this.isConnected = false;
    });

    this.socket.on("connect_error", (error) => {
      console.error("❌ Socket.IO connection error:", error);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error("❌ Max reconnection attempts reached");
        console.log("💡 Check server connectivity and CORS configuration");
      }
    });

    // Restaurant portal specific events
    this.socket.on("order_updated", (data) => {
      console.log("📦 Order updated:", data);
      this.eventHandlers.onOrderUpdated?.(data);
    });

    this.socket.on("order_received", (data) => {
      console.log("🆕 New order received:", data);
      this.eventHandlers.onOrderReceived?.(data);
    });

    this.socket.on("table_updated", (data) => {
      console.log("🪑 Table updated:", data);
      this.eventHandlers.onTableUpdated?.(data);
    });

    this.socket.on("food_item_updated", (data) => {
      console.log("🍽️ Food item updated:", data);
      this.eventHandlers.onFoodItemUpdated?.(data);
    });

    this.socket.on("dashboard_updated", (data) => {
      console.log("📊 Dashboard updated:", data);
      this.eventHandlers.onDashboardUpdated?.(data);
    });

    // Authentication events
    this.socket.on("authenticated", (data) => {
      console.log("✅ Socket authenticated:", data);
      this.eventHandlers.onAuthenticated?.(data);
    });

    this.socket.on("auth_error", (data) => {
      console.error("❌ Socket auth error:", data);
      this.eventHandlers.onAuthError?.(data);
    });

    this.socket.on("joined_restaurant", (data) => {
      console.log("🏪 Joined restaurant room:", data);
      this.eventHandlers.onJoinedRestaurant?.(data);
    });

    this.socket.on("error", (data) => {
      console.error("Socket error:", data);
      this.eventHandlers.onError?.(data);
    });
  }

  // Connect to socket server
  connect() {
    if (this.socket && !this.isConnected) {
      this.socket.connect();
    }
  }

  // Disconnect from socket server
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.isConnected = false;
    }
  }

  // Authenticate socket connection
  authenticate(token: string, userType?: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit("authenticate", { token, userType });
    } else {
      console.error("Socket not connected");
    }
  }

  // Join restaurant room
  joinRestaurant(restaurantId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit("join_restaurant", { restaurantId });
    } else {
      console.error("Socket not connected");
    }
  }

  // Update order status
  updateOrderStatus(orderId: string, newStatus: string, restaurantId: string, note?: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit("order_status_update", {
        orderId,
        newStatus,
        restaurantId,
        note,
      });
    } else {
      console.error("Socket not connected");
    }
  }

  // Update table status
  updateTableStatus(tableId: string, newStatus: string, restaurantId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit("table_status_update", {
        tableId,
        newStatus,
        restaurantId,
      });
    } else {
      console.error("Socket not connected");
    }
  }

  // Update food item availability
  updateFoodItemAvailability(itemId: string, isAvailable: boolean, restaurantId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit("food_item_availability_update", {
        itemId,
        isAvailable,
        restaurantId,
      });
    } else {
      console.error("Socket not connected");
    }
  }

  // Update dashboard
  updateDashboard(restaurantId: string, updateType: string, data: any) {
    if (this.socket && this.isConnected) {
      this.socket.emit("dashboard_update", {
        restaurantId,
        updateType,
        data,
      });
    } else {
      console.error("Socket not connected");
    }
  }

  // Set event handlers
  setEventHandlers(handlers: SocketEventHandlers) {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };
  }

  // Remove event handlers
  removeEventHandlers() {
    this.eventHandlers = {};
  }

  // Get connection status
  getConnectionStatus() {
    return this.isConnected;
  }

  // Get socket instance
  getSocket() {
    return this.socket;
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService; 